// src/services/google/client.js

const BASE_URL = 'https://www.googleapis.com';

/**
 * Generic fetch wrapper for Google APIs.
 * Handles Auth headers, standardizes errors, and detects 401 token expiry.
 * * @param {string} endpoint - e.g., '/tasks/v1/lists' or '/calendar/v3/calendars/primary/events'
 * @param {string} token - The OAuth Provider Token from the Supabase session.
 * @param {object} options - Standard fetch options (method, body, etc.).
 */
export async function googleFetch(endpoint, token, options = {}) {
  // 1. Safety Check: Do we have a token?
  if (!token) {
    // Return an error object or throw, depending on how you want to handle it in components.
    // Throwing forces calling code to use a try/catch.
    throw new Error('MISSING_TOKEN: Cannot call Google API without a provider token.');
  }

  // 2. Prepare Headers (ensure Content-Type is JSON for POST/PATCH/etc.)
  const headers = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    ...options.headers,
  };

  // 3. Execute Request
  const response = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  // 4. Handle "Token Expired" (Google sends 401)
  if (response.status === 401) {
    // This is the critical place to detect expired tokens.
    // The calling component can now refresh the session or redirect.
    throw new Error('TOKEN_EXPIRED'); 
  }

  // 5. Handle "No Content" (Successful delete/update often returns 204)
  if (response.status === 204) {
    return null;
  }

  // 6. Handle General API Errors (e.g., 400 Bad Request, 500 Server Error)
  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    // Use the error message from Google if available, otherwise use status text.
    const errorMessage = errorData.error?.message || `API Error (${response.status}): ${response.statusText}`;
    throw new Error(errorMessage);
  }

  // 7. Success
  return response.json();
}